import type { Lead, LeadStatus } from "@/domain/models/lead";
import type { CreateLeadInput, LeadRepository } from "@/domain/repositories/lead.repository";
import { LEADS } from "@/data/admin";
import { delay } from "@/infrastructure/repositories/mock/delay";

function cloneLeads(): Lead[] {
  return LEADS.map((lead) => ({ ...lead, tags: [...lead.tags] }));
}

export class MockLeadRepository implements LeadRepository {
  private items = cloneLeads();

  async list(): Promise<Lead[]> {
    await delay();
    return this.items.map((lead) => ({ ...lead, tags: [...lead.tags] }));
  }

  async getById(id: string): Promise<Lead | null> {
    await delay();
    const found = this.items.find((lead) => lead.id === id);
    return found ? { ...found, tags: [...found.tags] } : null;
  }

  async create(input: CreateLeadInput): Promise<Lead> {
    await delay();
    const now = new Date().toISOString();
    const lead: Lead = {
      ...input,
      id: `LEAD-${String(1000 + this.items.length + 1)}`,
      createdAt: now,
      updatedAt: now,
      tags: [...input.tags],
    };
    this.items = [lead, ...this.items];
    return { ...lead, tags: [...lead.tags] };
  }

  async updateStatus(id: string, status: LeadStatus): Promise<Lead> {
    return this.update(id, { status });
  }

  async update(id: string, input: Partial<Lead>): Promise<Lead> {
    await delay();
    const index = this.items.findIndex((lead) => lead.id === id);
    if (index < 0) throw new Error(`Lead not found: ${id}`);

    const current = this.items[index];
    const updated: Lead = {
      ...current,
      ...input,
      id,
      updatedAt: new Date().toISOString(),
      tags: input.tags ? [...input.tags] : [...current.tags],
    };
    this.items[index] = updated;
    return { ...updated, tags: [...updated.tags] };
  }
}
